import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import accountSettings from './en/account-settings.json';
import actionBuilder from './en/action-builder.json';
import actionBuilderDetail from './en/action-builder-detail.json';
import agentAbTest from './en/agent-ab-test.json';
import agentInsights from './en/agent-insights.json';
import agentLandingPage from './en/agent-landing-page.json';
import aiQaPage from './en/ai-qa-page.json';
import answersAnalytics from './en/answers-analytics.json';
import answersAnalyticsPage from './en/answers-analytics-page.json';
import app from './en/app.json';
import askForethought from './en/ask-forethought.json';
import assistConfig from './en/assist-config.json';
import assistInsights from './en/assist-insights.json';
import assistNotesPage from './en/assist-notes-page.json';
import assistPreview from './en/assist-preview.json';
import authenticationWall from './en/authentication-wall.json';
import autoflowV2Editor from './en/autoflow-v2-editor.json';
import autonomousAgentPreview from './en/autonomous-agent-preview.json';
import backendMessages from './en/backend-messages.json';
import builderParentPage from './en/builder-parent-page.json';
import common from './en/common.json';
import discover from './en/discover.json';
import discoverAutomationPage from './en/discover-automation-page.json';
import discoverTooltip from './en/discover-tooltip.json';
import discoverTopicDetailPage from './en/discover-topic-detail-page.json';
import events from './en/events.json';
import globalDiscover from './en/global-discover.json';
import globalWorkflowBuilder from './en/global-workflow-builder.json';
import homePage from './en/home-page.json';
import integrations from './en/integrations.json';
import integrationsPage from './en/integrations-page.json';
import intentConversationAnalytics from './en/intent-conversation-analytics.json';
import knowledge from './en/knowledge.json';
import orchestrator from './en/orchestrator.json';
import plan from './en/plan.json';
import predictIntents from './en/predict-intents.json';
import security from './en/security.json';
import solveAgentTesting from './en/solve-agent-testing.json';
import solveAnalytics from './en/solve-analytics.json';
import solveConfig from './en/solve-config.json';
import solveInsights from './en/solve-insights.json';
import solveInsightsArticleDetail from './en/solve-insights-article-detail.json';
import solveInsightsTopicDetail from './en/solve-insights-topic-detail.json';
import solveInsightsWorkflowDetail from './en/solve-insights-workflow-detail.json';
import solveWorkflows from './en/solve-workflows.json';
import ssoSettingsPage from './en/sso-settings-page.json';
import teamSettingsPage from './en/team-settings-page.json';
import triage from './en/triage.json';
import trialBanner from './en/trial-banner.json';
import trialExpired from './en/trial-expired.json';
import voiceInsights from './en/voice-insights.json';
import webCallInsights from './en/web-call-insights.json';
import workflowBuilder from './en/workflow-builder.json';
import workflowBuilderConfig from './en/workflow-builder-config.json';
import workflowBuilderEdit from './en/workflow-builder-edit.json';
import workflowBuilderPreview from './en/workflow-builder-preview.json';
import {
  getLocaleOverride,
  getSavedUserLocale,
  saveLocaleOverride,
  saveUserLocale,
} from './userLocale';

/**
 * Central i18n initialization.
 *
 * Namespaces live in `src/locales/en/<namespace>.json` and are registered below.
 * As features migrate their strings, add a line here:
 *
 *     import solveAnalytics from './en/solve-analytics.json';
 *     resources: { en: { solveAnalytics } }
 *
 * Usage in components:
 *
 *     const { t } = useTranslation('solve-analytics');
 *     t('filters.allMacros')
 *
 * See src/locales/README.md for the full migration guide.
 */

export const DEFAULT_NAMESPACE = 'ask-forethought';
export const FALLBACK_LANGUAGE = 'en';

void i18n.use(initReactI18next).init({
  defaultNS: DEFAULT_NAMESPACE,
  fallbackLng: FALLBACK_LANGUAGE,
  interpolation: {
    // React already escapes output; double-escaping corrupts apostrophes etc.
    escapeValue: false,
  },
  // Boot in the dev/test override (`?ft-locale=` / `ftLocale.set()`) if one
  // is active, else the locale the user was last seen with (persisted from
  // `ssel/users/me` — see applyUserLocale below) so the language is right
  // before any API responds. Falls back to English on first visit.
  lng: getLocaleOverride() ?? getSavedUserLocale() ?? FALLBACK_LANGUAGE,
  // Zendesk systems report locales in lowercase (`en-us`, `pt-br`); normalize
  // so resource lookup and `i18n.language` comparisons never depend on case.
  lowerCaseLng: true,
  ns: [
    'account-settings',
    'action-builder',
    'action-builder-detail',
    'agent-ab-test',
    'agent-insights',
    'agent-landing-page',
    'ai-qa-page',
    'answers-analytics',
    'answers-analytics-page',
    'app',
    'ask-forethought',
    'assist-config',
    'assist-insights',
    'assist-notes-page',
    'assist-preview',
    'authentication-wall',
    'autoflow-v2-editor',
    'autonomous-agent-preview',
    'backend-messages',
    'builder-parent-page',
    'common',
    'discover',
    'discover-automation-page',
    'discover-topic-detail-page',
    'discover-tooltip',
    'events',
    'global-discover',
    'global-workflow-builder',
    'home-page',
    'integrations',
    'integrations-page',
    'intent-conversation-analytics',
    'knowledge',
    'orchestrator',
    'plan',
    'predict-intents',
    'security',
    'solve-agent-testing',
    'solve-analytics',
    'solve-config',
    'solve-insights',
    'solve-insights-article-detail',
    'solve-insights-topic-detail',
    'solve-insights-workflow-detail',
    'solve-workflows',
    'sso-settings-page',
    'team-settings-page',
    'triage',
    'trial-banner',
    'trial-expired',
    'voice-insights',
    'web-call-insights',
    'workflow-builder',
    'workflow-builder-config',
    'workflow-builder-edit',
    'workflow-builder-preview',
  ],
  resources: {
    en: {
      'account-settings': accountSettings,
      'action-builder': actionBuilder,
      'action-builder-detail': actionBuilderDetail,
      'agent-ab-test': agentAbTest,
      'agent-insights': agentInsights,
      'agent-landing-page': agentLandingPage,
      'ai-qa-page': aiQaPage,
      'answers-analytics': answersAnalytics,
      'answers-analytics-page': answersAnalyticsPage,
      app,
      'ask-forethought': askForethought,
      'assist-config': assistConfig,
      'assist-insights': assistInsights,
      'assist-notes-page': assistNotesPage,
      'assist-preview': assistPreview,
      'authentication-wall': authenticationWall,
      'autoflow-v2-editor': autoflowV2Editor,
      'autonomous-agent-preview': autonomousAgentPreview,
      'backend-messages': backendMessages,
      'builder-parent-page': builderParentPage,
      common,
      discover,
      'discover-automation-page': discoverAutomationPage,
      'discover-tooltip': discoverTooltip,
      'discover-topic-detail-page': discoverTopicDetailPage,
      events,
      'global-discover': globalDiscover,
      'global-workflow-builder': globalWorkflowBuilder,
      'home-page': homePage,
      integrations,
      'integrations-page': integrationsPage,
      'intent-conversation-analytics': intentConversationAnalytics,
      knowledge,
      orchestrator,
      plan,
      'predict-intents': predictIntents,
      security,
      'solve-agent-testing': solveAgentTesting,
      'solve-analytics': solveAnalytics,
      'solve-config': solveConfig,
      'solve-insights': solveInsights,
      'solve-insights-article-detail': solveInsightsArticleDetail,
      'solve-insights-topic-detail': solveInsightsTopicDetail,
      'solve-insights-workflow-detail': solveInsightsWorkflowDetail,
      'solve-workflows': solveWorkflows,
      'sso-settings-page': ssoSettingsPage,
      'team-settings-page': teamSettingsPage,
      triage,
      'trial-banner': trialBanner,
      'trial-expired': trialExpired,
      'voice-insights': voiceInsights,
      'web-call-insights': webCallInsights,
      'workflow-builder': workflowBuilder,
      'workflow-builder-config': workflowBuilderConfig,
      'workflow-builder-edit': workflowBuilderEdit,
      'workflow-builder-preview': workflowBuilderPreview,
    },
  },
  returnNull: false,
});

/**
 * Apply the locale the backend reports for the signed-in user
 * (`ssel/users/me` → `user.locale`): persist it for the next boot and
 * switch i18next live. With only `en` resources bundled today the switch
 * is invisible — every key falls back to English via `fallbackLng` — but
 * the plumbing means new locale resources take effect the moment they're
 * registered.
 */
export function applyUserLocale(locale: string | null | undefined): void {
  if (!locale) {
    return;
  }
  const normalized = locale.toLowerCase();
  saveUserLocale(normalized);
  // A dev/test override always wins — remember the real locale (above) so
  // clearing the override lands back on it, but don't switch away from the
  // override when users/me responds.
  if (getLocaleOverride()) {
    return;
  }
  if (i18n.language !== normalized) {
    void i18n.changeLanguage(normalized);
  }
}

/**
 * Console helper for previewing the dashboard in another language without
 * changing the signed-in user's locale (also reachable via the
 * `?ft-locale=<locale>` URL param; `?ft-locale=off` clears it):
 *
 *     ftLocale.set('fr-fr')   // preview French (falls back to English
 *                             // per-key until fr resources ship)
 *     ftLocale.set('cimode')  // i18next test mode: renders raw keys, so
 *                             // still-hardcoded English stands out
 *     ftLocale.clear()        // back to the real user locale
 *     ftLocale.current()      // what's active right now
 *
 * The override persists in localStorage until cleared.
 */
export const ftLocale = {
  clear(): void {
    saveLocaleOverride(undefined);
    void i18n.changeLanguage(getSavedUserLocale() ?? FALLBACK_LANGUAGE);
  },
  current(): string {
    return i18n.language;
  },
  set(locale: string): void {
    const normalized = locale.toLowerCase();
    saveLocaleOverride(normalized);
    void i18n.changeLanguage(normalized);
  },
};

declare global {
  interface Window {
    ftLocale?: typeof ftLocale;
  }
}
window.ftLocale = ftLocale;

export default i18n;
